import { ErrorMessage, useFormikContext } from 'formik';
import { Link1 } from 'iconsax-react';
import { object, string } from 'yup';

import { CustomErrorMessage } from '@design-system/error-message';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@design-system/select';
import { Textfield } from '@shared/fields/text-field';

import {
  WEBSITE_MODES,
  knowledgeWebsitesApi,
  type KnowledgeWebsiteMode,
} from '../services/knowledge-content-api';
import { ContentFormDialog } from './content-form-dialog';

type Values = { url: string; mode: KnowledgeWebsiteMode };

const schema = object({
  url: string()
    .trim()
    .required('URL is required')
    .matches(/^https?:\/\/\S+$/i, 'Enter a full http(s) URL'),
  mode: string().oneOf(WEBSITE_MODES.map((mode) => mode.value)),
});

function ModeField() {
  const { values, setFieldValue } = useFormikContext<Values>();
  return (
    <Select
      value={values.mode}
      onValueChange={(value) => setFieldValue('mode', value)}
    >
      <SelectTrigger
        aria-label='Crawl mode'
        className='h-12 text-gray-800 rounded-none border-0 border-t border-pale-border bg-transparent px-4 text-[15px] focus:ring-0'
      >
        <SelectValue placeholder='How far to crawl' />
      </SelectTrigger>
      <SelectContent>
        {WEBSITE_MODES.map((mode) => (
          <SelectItem
            key={mode.value}
            value={mode.value}
          >
            {mode.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function WebsiteDialog({
  kbaseId,
  open,
  onOpenChange,
}: {
  kbaseId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const create = knowledgeWebsitesApi.useCreate();

  return (
    <ContentFormDialog<Values>
      open={open}
      onOpenChange={onOpenChange}
      title='Add URL'
      subtitle='Train Your Model On URLs'
      editing={false}
      isPending={create.isPending}
      initialValues={{ url: '', mode: 'DOMAIN' }}
      validationSchema={schema}
      onSubmit={(values) =>
        create.mutateAsync({
          kbase: kbaseId,
          url: values.url.trim(),
          mode: values.mode,
        })
      }
    >
      <div>
        <div className='overflow-hidden rounded-xl border border-pale-border'>
          <Textfield
            name='url'
            type='url'
            placeholder='Enter URL'
            aria-label='Enter URL'
            hideError
            variant='outline'
            className='h-12 rounded-none border-0 bg-primary-30 px-4'
            prefixIcon={
              <Link1
                size={20}
                className='text-gray-700'
              />
            }
          />
          <ModeField />
        </div>
        <ErrorMessage
          name='url'
          component={CustomErrorMessage}
        />
      </div>
    </ContentFormDialog>
  );
}
